import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useAuth } from "@/lib/auth/auth-provider";

export function SignUpPage() {
  const { signUp, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [confirmRequired, setConfirmRequired] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (user) {
    void navigate({ to: "/select-tenant", replace: true });
  }

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    const { error: err, emailConfirmRequired } = await signUp(email, password, fullName);
    if (err) {
      setError(err);
      setSubmitting(false);
      return;
    }
    if (emailConfirmRequired) {
      setConfirmRequired(true);
      setSubmitting(false);
      return;
    }
    await navigate({ to: "/select-tenant", replace: true });
  }

  if (confirmRequired) {
    return (
      <main className="flex min-h-dvh items-center justify-center bg-neutral-950 px-6 text-neutral-100">
        <div className="max-w-sm space-y-3 text-center">
          <h1 className="text-xl font-semibold">Check your inbox</h1>
          <p className="text-sm text-neutral-400">
            We sent a confirmation link to <span className="text-neutral-200">{email}</span>. Click
            it to finish signing up, then return here to sign in.
          </p>
          <Link to="/sign-in" className="inline-block text-xs text-neutral-300 hover:underline">
            Back to sign in
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="flex min-h-dvh items-center justify-center bg-neutral-950 px-6 text-neutral-100">
      <div className="w-full max-w-sm space-y-6">
        <header className="space-y-1 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">Argus</h1>
          <p className="text-sm text-neutral-400">Create your account</p>
        </header>

        <form
          onSubmit={(e) => {
            void onSubmit(e);
          }}
          className="space-y-4"
        >
          <div className="space-y-1.5">
            <label htmlFor="full-name" className="text-xs uppercase tracking-wide text-neutral-400">
              Full name
            </label>
            <input
              id="full-name"
              type="text"
              required
              autoComplete="name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full rounded border border-neutral-800 bg-neutral-900 px-3 py-2 text-sm outline-none focus:border-neutral-600"
            />
          </div>

          <div className="space-y-1.5">
            <label htmlFor="email" className="text-xs uppercase tracking-wide text-neutral-400">
              Email
            </label>
            <input
              id="email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded border border-neutral-800 bg-neutral-900 px-3 py-2 text-sm outline-none focus:border-neutral-600"
            />
          </div>

          <div className="space-y-1.5">
            <label htmlFor="password" className="text-xs uppercase tracking-wide text-neutral-400">
              Password
            </label>
            <input
              id="password"
              type="password"
              required
              minLength={12}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded border border-neutral-800 bg-neutral-900 px-3 py-2 text-sm outline-none focus:border-neutral-600"
            />
            <p className="text-[11px] text-neutral-500">12+ characters.</p>
          </div>

          {error ? (
            <div className="rounded border border-red-900/60 bg-red-950/30 px-3 py-2 text-xs text-red-300">
              {error}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded bg-neutral-100 px-4 py-2 text-sm font-medium text-neutral-950 transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? "Creating account…" : "Create account"}
          </button>
        </form>

        <p className="text-center text-xs text-neutral-500">
          Already have an account?{" "}
          <Link to="/sign-in" className="text-neutral-300 underline-offset-2 hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </main>
  );
}
